// Reine Helfer für den CRM-Kalender (Monat/Woche/Tag).
// Quellen: Aufgaben (Fälligkeit), Serviceberichte (Servicebesuch), Seriennummern (Ablauf).

export const pad = (n) => String(n).padStart(2, "0");
export const dateKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export function startOfWeek(date) {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // Montag = 0
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

// Baut eine flache, nach Datum gruppierte Event-Map.
export function buildEventsByDate({ tasks = [], reports = [], serials = [] }) {
  const map = {};
  const push = (key, ev) => {
    if (!key) return;
    if (!map[key]) map[key] = [];
    map[key].push(ev);
  };

  tasks.forEach((t) => {
    if (!t.due_date) return;
    push(t.due_date.substring(0, 10), {
      type: "task",
      id: t.id,
      title: t.title,
      priority: t.priority,
      status: t.status,
      customer: t.customer_name,
      assignee: t.assigned_to_user_name,
    });
  });

  reports.forEach((r) => {
    if (!r.service_date) return;
    push(r.service_date.substring(0, 10), {
      type: "visit",
      id: r.id,
      title: r.title,
      customer: r.customer_name,
      technician: r.technician_name,
      signed: !!r.signed_at,
      startTime: r.start_time,
      endTime: r.end_time,
    });
  });

  serials.forEach((s) => {
    if (!s.expiry_date) return;
    push(s.expiry_date.substring(0, 10), {
      type: "serial",
      id: `sn-${s.id}`,
      title: s.serial_number,
      product: s.product_name,
      customer: s.customer_name,
    });
  });

  // Innerhalb eines Tages sortieren: Besuche (mit Uhrzeit) zuerst, dann Aufgaben, dann Abläufe
  const order = { visit: 0, task: 1, serial: 2 };
  Object.values(map).forEach((arr) =>
    arr.sort((a, b) => {
      if (order[a.type] !== order[b.type]) return order[a.type] - order[b.type];
      return (a.startTime || "").localeCompare(b.startTime || "");
    })
  );
  return map;
}

// Monatsraster (6 Wochen), Montag-basiert.
export function buildMonthGrid(viewDate, eventsByDate) {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const first = new Date(year, month, 1);
  const gridStart = startOfWeek(first);
  const todayKey = dateKey(new Date());
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = addDays(gridStart, i);
    const key = dateKey(d);
    cells.push({
      key,
      day: d.getDate(),
      date: d,
      inMonth: d.getMonth() === month,
      isToday: key === todayKey,
      events: eventsByDate[key] || [],
    });
    if (i >= 34 && d.getMonth() !== month && addDays(gridStart, i + 1).getMonth() !== month) break;
  }
  return {
    year,
    month,
    label: first.toLocaleDateString("de-DE", { month: "long", year: "numeric" }),
    cells,
  };
}

export function buildWeek(viewDate, eventsByDate) {
  const start = startOfWeek(viewDate);
  const todayKey = dateKey(new Date());
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = addDays(start, i);
    const key = dateKey(d);
    days.push({
      key,
      date: d,
      isToday: key === todayKey,
      weekday: d.toLocaleDateString("de-DE", { weekday: "short" }),
      dayNum: d.getDate(),
      events: eventsByDate[key] || [],
    });
  }
  const end = addDays(start, 6);
  const label = `${start.toLocaleDateString("de-DE", { day: "numeric", month: "short" })} – ${end.toLocaleDateString("de-DE", { day: "numeric", month: "short", year: "numeric" })}`;
  return { start, days, label };
}

export function buildDay(viewDate, eventsByDate) {
  const key = dateKey(viewDate);
  return {
    key,
    isToday: key === dateKey(new Date()),
    label: viewDate.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" }),
    events: eventsByDate[key] || [],
  };
}

// Farb-/Stilklassen je Event-Typ (für Badges/Pills).
export function eventStyle(ev) {
  if (ev.type === "visit") {
    return "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200";
  }
  if (ev.type === "serial") {
    return "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/40 dark:text-orange-200";
  }
  if (ev.status === "Erledigt") {
    return "bg-slate-100 text-slate-500 border-slate-200 line-through dark:bg-slate-800 dark:text-slate-400";
  }
  switch (ev.priority) {
    case "Hoch":
      return "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-200";
    case "Mittel":
      return "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-200";
    default:
      return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200";
  }
}
